<template>
  <page-template :is-loading="isLoading" :data-exists="selectedRateLimitRuleData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('rateLimitRule.configPage.newPageHeadline')
          : $t('rateLimitRule.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Rate Limit Rules List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedRateLimitRule && !isNew">
        <div class="vertical-divider">
          <layout-divider-line color="light-gray" orientation="vertical" />
        </div>
        <form-dropdown
          v-model:value="selectedRateLimitRule"
          :disabled="isLoading"
          :header-tooltip="$t('rateLimitRule.configPage.switchTooltip')"
          :ignore-update-in-process="true"
          :items="dropdownData.map((item) => [item.id, item.name]) as [string | number, string][]"
          classes="switch-rate-limit-rule switch-page-entry"
          data-qa="switch-rate-limit-rule"
          type="single-select"
          @update:value="switchRateLimitRule($event as string)"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedRateLimitRule && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateRateLimitRule()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadRateLimitRule()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteRateLimitRuleModal()"
          class="delete-button"
          :disabled="isLoading || selectedRateLimitRuleProtected || selectedRateLimitRuleReferenced"
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isRateLimitRuleValid"
        :tooltip="saveTooltip"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('rateLimitRule.configPage.name')"
              :placeholder="$t('rateLimitRule.configPage.name')"
              v-model:inputValue="selectedRateLimitRuleData.name"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field rbz-field-toggle">
            <rbz-form-toggle
              v-model:buttonValue="selectedRateLimitRuleData.active"
              :label="$t('rateLimitRule.configPage.active')"
              data-qa="active-toggle"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field rbz-field-toggle">
            <rbz-form-toggle
              v-model:buttonValue="selectedRateLimitRuleData.global"
              :label="$t('rateLimitRule.configPage.global')"
              data-qa="global-toggle"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('rateLimitRule.configPage.description')"
              v-model:inputValue="selectedRateLimitRuleData.description"
              rows="2"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field count-by-field" :class="{ 'is-active': countByFieldActive }">
            <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
              {{ $t('rateLimitRule.configPage.countBy') }}
            </label>
            <key-option
              v-for="(keyOption, index) in countByKeyOptions"
              :key="keyOption.id"
              show-remove
              :removable="countByKeyOptions.length > 1"
              @remove="removeKey(index)"
              v-model:option="countByKeyOptions[index].value"
              @focusin="countByFieldActive = true"
              @focusout="countByFieldActive = false"
              :disabled="readOnly"
            />
            <div v-if="countByError" class="key-invalid rbz-text-medium rbz-text-danger mb-2">
              {{ countByError }}
            </div>
            <rbz-form-button
              :label="$t('rateLimitRule.configPage.countByNewEntry')"
              class="new-count-by-button"
              data-qa="add-count-by-button"
              variant="secondary"
              @button-click="addKey()"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field event-field" :class="{ 'is-active': eventByFieldActive }">
            <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
              {{ $t('rateLimitRule.configPage.event') }}
            </label>
            <key-option
              v-model:option="selectedRateLimitRuleData.pairwith"
              use-default-self
              @focusin="eventByFieldActive = true"
              @focusout="eventByFieldActive = false"
              :disabled="readOnly"
            />
            <div v-if="eventError" class="key-invalid rbz-text-medium rbz-text-danger mb-2">
              {{ eventError }}
            </div>
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="threshold"
              data-qa="threshold-input"
              classes="threshold"
              type="number"
              number-type="whole"
              :label="$t('rateLimitRule.configPage.threshold')"
              :placeholder="$t('rateLimitRule.configPage.threshold')"
              :inputValue="selectedRateLimitRuleData.threshold"
              @update:inputValue="
                selectedRateLimitRuleData.threshold = String($event).trim() ? Number($event) : NaN
              "
              :is-invalid="!isThresholdValid"
              :error-message-text="thresholdError"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="timeframe"
              data-qa="timeframe-input"
              class="timeframe-input"
              type="number"
              number-type="whole"
              :label="$t('rateLimitRule.configPage.timeFrame')"
              :placeholder="$t('rateLimitRule.configPage.timeFrame')"
              suffix="seconds"
              v-model:inputValue="selectedRateLimitRuleData.timeframe"
              :is-invalid="!isTimeframeValid"
              :error-message-text="$t('rateLimitRule.configPage.timeFrameInvalid')"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <form-dropdown
              v-if="actions.length"
              :items="actions.map((item) => [item.id, item.name]) as [string | number, string][]"
              v-model:value="selectedRateLimitRuleData.action"
              :label="$t('rateLimitRule.configPage.action')"
              data-qa="action-dropdown"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field rbz-field-toggle">
            <rbz-form-toggle
              v-model:buttonValue="selectedRateLimitRuleData['is_action_ban']"
              @update:button-value="normalizeBanTtl()"
              class="ban-field"
              data-qa="ban-toggle"
              :label="$t('rateLimitRule.configPage.ban')"
              :disabled="readOnly"
            />
          </div>
          <div v-if="selectedRateLimitRuleData['is_action_ban']" class="rbz-field">
            <rbz-form-input
              name="ban-duration"
              type="number"
              number-type="whole"
              classes="ban-duration"
              :label="$t('rateLimitRule.configPage.banDuration')"
              :placeholder="$t('rateLimitRule.configPage.banDuration')"
              data-qa="ban-duration-input"
              suffix="minutes"
              :inputValue="selectedRateLimitRuleData.ttl / 60"
              @update:inputValue="selectedRateLimitRuleData.ttl = Number($event) * 60"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <tags-selection
              :value="selectedRateLimitRuleData.tags"
              @update:value="selectedRateLimitRuleData.tags = $event as string[]"
              :label="$t('rateLimitRule.configPage.tags')"
              :disabled="readOnly"
            />
            <div class="automatic-tags">
              <template v-for="tag in automaticTags" :key="tag">
                <rbz-custom-tag :value="tag" :disabled="readOnly" />
              </template>
            </div>
          </div>
        </div>
        <div class="rbz-column is-8">
          <tags-selection-table
            :columns="tagsSelectionTableColumns"
            :show-slider-toggle="true"
            @update:tags="updateTags($event.fieldName as IncludeExcludeType, $event.tags)"
            @update:tags-relation="
              updateRelation($event.fieldName as IncludeExcludeType, $event.relation)
            "
            :read-only="readOnly"
          >
          </tags-selection-table>
        </div>
      </div>
      <rbz-card-collapsible
        :is-collapsed="false"
        class="security-policies-connections-card"
        data-qa="security-policies-connections-card"
      >
        <template #title>
          {{ $t('rateLimitRule.configPage.securityPoliciesConnections.title') }}
        </template>
        <template #content>
          <rbz-table
            :columns="connectionsColumns"
            :table-data="connections"
            ref="connectionsTable"
            :row-buttons="connectionsRowButtons"
            rows-per-page="20"
            data-qa="connections-table"
            :show-new-button="!readOnly"
            :show-new-as-row="true"
            :show-buttons-column="!readOnly"
            :show-download-button="false"
            :show-filter-button="false"
            :new-button-label="
              $t('rateLimitRule.configPage.securityPoliciesConnections.newButtonLabel')
            "
            :show-new-default-data="getConnectionDefaultValues()"
            @add-inline-data="addConnection($event as NewConnection)"
            @delete-row-button-clicked="removeConnection($event as Connection)"
          >
            <template #newRowOverride="newRowProps">
              <template v-if="newConnectionSecurityPolicies?.length === 1">
                <td class="all-connected-data-cell" colspan="3">
                  <div class="data-cell-content ellipsis has-text-centered">
                    <span>
                      {{ $t('rateLimitRule.configPage.securityPoliciesConnections.allConnected') }}
                    </span>
                  </div>
                </td>
                <td>
                  <form-confirmation :show-confirm="false" @cancel="cancelAddConnection()" />
                </td>
              </template>
              <template v-else>
                <td class="new-data-cell">
                  <form-dropdown
                    class="new-security-policy-selection name-selection"
                    :items="
                      newConnectionSecurityPolicies.map((item) => {
                        return [item.id, item.name];
                      }) as [string | number, string][]
                    "
                    :skip-sort="true"
                    data-qa="new-connection-security-policy-dropdown"
                    v-model:value="newRowProps.data.securityPolicyId"
                    @update:value="
                      newConnectionOnChangeSecurityPolicy(newRowProps.data as NewConnection)
                    "
                  />
                </td>
                <td class="new-data-cell">
                  <div class="connection-level-radio-wrapper">
                    <rbz-form-radio
                      v-if="
                        newRowProps.data.securityPolicyId !== 'rbz-add-all-security-policies-option'
                      "
                      name="entry-connection-level"
                      id="entry-connection-level-path"
                      data-qa="entry-connection-level-path-radio"
                      v-model:model="newRowProps.data.connectionLevel"
                      :label="
                        $t(
                          'rateLimitRule.configPage.securityPoliciesConnections.connectionLevel.path'
                        )
                      "
                      @input="
                        newConnectionOnChangeConnectionLevel(
                          newRowProps.data as NewConnection,
                          'path'
                        )
                      "
                      :checked="newRowProps.data.connectionLevel === 'path'"
                    />
                    <rbz-form-radio
                      name="entry-connection-level"
                      id="entry-connection-level-site"
                      data-qa="entry-connection-level-site-radio"
                      v-model:model="newRowProps.data.connectionLevel"
                      :label="
                        $t(
                          'rateLimitRule.configPage.securityPoliciesConnections.connectionLevel.site'
                        )
                      "
                      @input="
                        newConnectionOnChangeConnectionLevel(
                          newRowProps.data as NewConnection,
                          'site'
                        )
                      "
                      :checked="newRowProps.data.connectionLevel === 'site'"
                      :disabled="
                        newConnectionCheckConnectedAnyEntry(newRowProps.data.securityPolicyId)
                      "
                    />
                    <rbz-form-radio
                      name="entry-connection-level"
                      id="entry-connection-level-all"
                      data-qa="entry-connection-level-all-radio"
                      :label="
                        $t(
                          'rateLimitRule.configPage.securityPoliciesConnections.connectionLevel.all'
                        )
                      "
                      v-b-tooltip="
                        $t(
                          'rateLimitRule.configPage.securityPoliciesConnections.connectionLevel.allTooltip'
                        )
                      "
                      @input="
                        newConnectionOnChangeConnectionLevel(
                          newRowProps.data as NewConnection,
                          'all'
                        )
                      "
                      :checked="newRowProps.data.connectionLevel === 'all'"
                    />
                  </div>
                </td>
                <td class="new-data-cell">
                  <form-dropdown
                    type="multi-select"
                    class="new-security-policy-entry-selection match-selection"
                    :items="
                      newConnectionListSecurityPolicyEntries(newRowProps.data.securityPolicyId).map(
                        (item) => {
                          return [item.id, item.match];
                        }
                      ) as [string | number, string][]
                    "
                    :skip-sort="true"
                    v-if="newRowProps.data.connectionLevel === 'path'"
                    data-qa="new-security-policy-entry-dropdown"
                    v-model:value="newRowProps.data.entryIds"
                  />
                </td>
              </template>
            </template>
            <template
              #newRowOverrideConfirmationButtons
              v-if="newConnectionSecurityPolicies?.length === 1"
            >
              <template></template>
            </template>
          </rbz-table>
        </template>
      </rbz-card-collapsible>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadRateLimitRule(selectedRateLimitRule);
          loadDropdownData();
        "
        :disabled="readOnly"
      />
      <rbz-modal-dialog
        :show="showDeleteRateLimitRuleModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteRateLimitRule"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteRateLimitRuleModal = false"
        @closeModal="showDeleteRateLimitRuleModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', { name: selectedRateLimitRuleData.name })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import {
  Action,
  BasicDocument,
  DocumentName,
  GenericObject,
  KeyOptionData,
  RateLimitRule,
  SecurityPolicy,
  SecurityPolicyEntryMatch,
  TagsSelectionTableColumn,
  IncludeExcludeType,
  Relation,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import {
  shouldUseMockData,
  getRateLimitRulesMockData,
  getSecurityPoliciesMockData,
  getActionsMockData,
} from '@/helpers/MockDataUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import TagsSelection from '@/components/TagsSelection.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import RbzCustomTag from '@/components/RbzCustomTag.vue';
import KeyOption from '@/components/KeyOption.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import GitHistory from '@/components/GitHistory.vue';
import RbzCardCollapsible from 'ui-component-library/rbz/components/RbzCardCollapsible.vue';
import TagsSelectionTable from '@/components/TagsSelectionTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { useTagsStore } from '@/store/TagsStore';
import { usePlanetStore } from '@/store/PlanetStore';
import FormConfirmation from 'ui-component-library/rbz/components/FormConfirmation.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import RbzFormRadio from '@/components/RbzFormRadio.vue';
import FormDropdown from 'ui-component-library/rbz/components/FormDropdown.vue';
import { useRouting } from '@/helpers/RoutingUtils';

type Connection = {
  name: string;
  id: string;
  securityPolicyId: string;
  entryId: string;
  entryName: string;
  entryMatch: string;
};

type NewConnection = {
  securityPolicyId: SecurityPolicy['id'];
  connectionLevel: ConnectionLevel;
  entryIds: SecurityPolicyEntryMatch['id'][];
};

type ConnectionLevel = 'path' | 'site' | 'all';

function validateKeyOptionName(keyType: string, keyValue: string): 'empty' | 'spaces' | '' {
  let nameToCheck: string;
  if (['args', 'cookies', 'headers'].includes(keyType)) {
    nameToCheck = keyValue || '';
  } else if (keyType === 'plugins') {
    const parts = (keyValue || '').split('.');
    nameToCheck = parts.slice(1).join('.');
  } else {
    return '';
  }
  const trimmed = nameToCheck.trim();
  if (!trimmed) {
    return 'empty';
  }
  if (/\s/.test(trimmed)) {
    return 'spaces';
  }
  return '';
}

export default defineComponent({
  name: 'RateLimitRulesEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    FormDropdown,
    RbzFormRadio,
    FormConfirmation,
    RbzFormInput,
    RbzFormTextarea,
    RbzFormToggle,
    GitHistory,
    KeyOption,
    LayoutDividerLine,
    PageTemplate,
    RbzCardCollapsible,
    RbzCustomTag,
    RbzFormButton,
    RbzModalDialog,
    RbzTable,
    TagsSelection,
    TagsSelectionTable,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedRateLimitRule: null as string,
      selectedRateLimitRuleData: null as RateLimitRule,
      originalRateLimitRuleData: null as RateLimitRule,
      countByKeyOptions: null as KeyOptionData[],
      pageLoadingCounter: 0,
      isDeleteLoading: false,
      showDeleteRateLimitRuleModal: false,

      actions: null as Action[],
      securityPolicies: null as SecurityPolicy[],

      connectionsOriginal: null as Connection[],
      connections: null as Connection[],
      connectionsColumns: [
        {
          id: 'name',
          title: this.$t('rateLimitRule.configPage.securityPoliciesConnections.name'),
          fieldNames: ['name'],
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'entry-name',
          title: this.$t('rateLimitRule.configPage.securityPoliciesConnections.entryName'),
          fieldNames: ['entryName'],
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'entry-match',
          title: this.$t('rateLimitRule.configPage.securityPoliciesConnections.entryMatch'),
          fieldNames: ['entryMatch'],
          displayFunction: (item: Connection) => {
            return item.entryMatch !== '__site_level__' ? item.entryMatch : '';
          },
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      connectionsRowButtons: [
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      countByFieldActive: false,
      eventByFieldActive: false,
    };
  },
  watch: {
    countByKeyOptions: {
      handler: function () {
        if (this.selectedRateLimitRuleData) {
          this.selectedRateLimitRuleData.key = _.map(this.countByKeyOptions, 'value');
        }
      },
      deep: true,
    },
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useTagsStore, usePlanetStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName(): DocumentName {
      return {
        plural: this.$t('rateLimitRule.plural'),
        singular: this.$t('rateLimitRule.singular'),
      };
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/rate-limit-rules/versions`;
    },

    isLoading(): boolean {
      return this.pageLoadingCounter > 0 || this.isDeleteLoading;
    },

    selectedRateLimitRuleProtected(): boolean {
      return this.selectedRateLimitRule?.startsWith('rl-');
    },

    selectedRateLimitRuleReferenced(): boolean {
      return this.connectionsOriginal?.length > 0;
    },

    deleteTooltip(): string {
      if (this.selectedRateLimitRuleProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.selectedRateLimitRuleReferenced) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return '';
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedRateLimitRuleData, this.originalRateLimitRuleData);
    },

    automaticTags(): string[] {
      const name = this.tagsStore.textToTag(this.selectedRateLimitRuleData.name);
      const nameTag = `limit-name:${name}`;
      return [nameTag];
    },

    tagsSelectionTableColumns(): TagsSelectionTableColumn[] {
      return [
        {
          title: this.$t('common.include'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'include',
              tags: this.selectedRateLimitRuleData['include'].tags,
              relation: this.selectedRateLimitRuleData['include'].relation,
              tagVariant: (tag: string) => {
                if (this.duplicateTags.includes(tag)) {
                  return 'error';
                }
                return 'fill';
              },
            },
          ],
          classes: 'is-6',
        },
        {
          title: this.$t('common.exclude'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'exclude',
              tags: this.selectedRateLimitRuleData['exclude'].tags,
              relation: this.selectedRateLimitRuleData['exclude'].relation,
              tagVariant: (tag: string) => {
                if (this.duplicateTags.includes(tag)) {
                  return 'error';
                }
                return 'fill';
              },
            },
          ],
          classes: 'is-6',
        },
      ];
    },

    duplicateTags(): string[] {
      if (!this.selectedRateLimitRuleData) {
        return [];
      }
      const allTags = _.concat(
        this.selectedRateLimitRuleData['include'].tags,
        this.selectedRateLimitRuleData['exclude'].tags
      );
      return _.filter(allTags, (val, i, iteratee) => _.includes(iteratee, val, i + 1));
    },

    isCountByValid(): boolean {
      let isCountByValid;
      const keysToCheck = _.countBy(this.selectedRateLimitRuleData.key, (item) => {
        const key = Object.keys(item)[0];
        return `${key}_${item[key]}`;
      });
      isCountByValid = true;
      for (const key of Object.keys(keysToCheck)) {
        if (keysToCheck[key] > 1) {
          isCountByValid = false;
          break;
        }
      }
      return isCountByValid;
    },

    countByError(): string {
      for (const keyOption of this.selectedRateLimitRuleData.key) {
        const keyType = Object.keys(keyOption)[0];
        const nameError = validateKeyOptionName(keyType, keyOption[keyType]);
        if (nameError === 'empty') {
          return this.$t('rateLimitRule.configPage.countByInvalidEmpty');
        }
        if (nameError === 'spaces') {
          return this.$t('rateLimitRule.configPage.countByInvalidSpaces');
        }
      }
      if (!this.isCountByValid) {
        return this.$t('rateLimitRule.configPage.countByInvalidMustBeUnique');
      }
      return '';
    },

    isCountByNamesValid(): boolean {
      return !this.countByError;
    },

    eventError(): string {
      const pairWith = this.selectedRateLimitRuleData.pairwith;
      if (!pairWith || Object.keys(pairWith).length === 0) {
        return '';
      }
      const keyType = Object.keys(pairWith)[0];
      const nameError = validateKeyOptionName(keyType, pairWith[keyType]);
      if (nameError === 'empty') {
        return this.$t('rateLimitRule.configPage.eventInvalidEmpty');
      }
      if (nameError === 'spaces') {
        return this.$t('rateLimitRule.configPage.eventInvalidSpaces');
      }
      return '';
    },

    isEventValid(): boolean {
      return !this.eventError;
    },

    thresholdError(): string {
      const threshold = this.selectedRateLimitRuleData.threshold;
      if (isNaN(threshold)) {
        return this.$t('rateLimitRule.configPage.thresholdInvalidEmpty');
      }
      if (threshold < 0) {
        return this.$t('rateLimitRule.configPage.thresholdInvalidNegative');
      }
      if (!Number.isInteger(threshold)) {
        return this.$t('rateLimitRule.configPage.thresholdInvalidDecimal');
      }
      return '';
    },

    isThresholdValid(): boolean {
      return !this.thresholdError;
    },

    newConnectionSecurityPolicies(): (SecurityPolicy | Pick<SecurityPolicy, 'id' | 'name'>)[] {
      let securityPolicies: (SecurityPolicy | Pick<SecurityPolicy, 'id' | 'name'>)[] =
        this.securityPolicies?.filter((securityPolicy) => {
          const notAllPathsConnected =
            this.newConnectionListSecurityPolicyEntries(securityPolicy.id).length > 0;
          const siteLevelConnected = this.newConnectionCheckConnectedSiteLevel(securityPolicy.id);
          return notAllPathsConnected && !siteLevelConnected;
        }) ?? [];
      securityPolicies = _.sortBy(securityPolicies, (item) => {
        return (item as SecurityPolicy).name?.toLowerCase();
      }) as (SecurityPolicy | Pick<SecurityPolicy, 'id' | 'name'>)[];
      securityPolicies.unshift({
        id: 'rbz-add-all-security-policies-option',
        name: this.$t('rateLimitRule.configPage.securityPoliciesConnections.connectToAll'),
      });
      return securityPolicies;
    },

    isActionValid(): boolean {
      const banDurationInvalid =
        this.selectedRateLimitRuleData['is_action_ban'] &&
        (this.selectedRateLimitRuleData.ttl / 60 <= 0 ||
          this.selectedRateLimitRuleData.ttl / 60 > 1440);
      const unselectedAction = _.isNil(this.selectedRateLimitRuleData.action);
      return !banDurationInvalid && !unselectedAction;
    },

    isTimeframeValid(): boolean {
      return this.selectedRateLimitRuleData?.timeframe > 0;
    },

    isRateLimitRuleValid(): boolean {
      if (!this.selectedRateLimitRuleData) {
        return false;
      }
      const noDuplicates = this.duplicateTags.length === 0;
      const trimmedName = this.selectedRateLimitRuleData.name?.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedRateLimitRuleData.name &&
        (trimmedName === this.originalRateLimitRuleData.name.toLowerCase().trim() || !nameExists);
      return (
        noDuplicates &&
        this.isCountByNamesValid &&
        this.isEventValid &&
        this.isThresholdValid &&
        nameValid &&
        this.isActionValid &&
        this.isTimeframeValid
      );
    },

    saveTooltip(): string {
      if (this.isLoading) {
        return '';
      }
      if (!this.isTimeframeValid) {
        return this.$t('rateLimitRule.configPage.timeFrameInvalid');
      }
      if (!this.isThresholdValid) {
        return this.thresholdError;
      }
      if (!this.isCountByNamesValid) {
        return this.countByError;
      }
      if (!this.isEventValid) {
        return this.eventError;
      }
      return '';
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('rate-limit-rules'));
    },

    switchRateLimitRule(rateLimitRuleId: string) {
      this.navigateTo(this.getEditPath('rate-limit-rules', rateLimitRuleId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getRateLimitRulesMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/rate-limit-rules`,
          config: { headers: { 'x-fields': 'id, name' } },
        });
      }
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newRateLimitRule(): RateLimitRule {
      const factory = DatasetsUtils.newEntityFactory['rate-limit-rules'];
      return factory && factory();
    },

    async loadRateLimitRule(rateLimitRuleId: string) {
      this.setPageLoadingStatus(true);
      this.selectedRateLimitRule = rateLimitRuleId;
      let response;
      if (shouldUseMockData()) {
        const items = getRateLimitRulesMockData()?.items || [];
        const found = items.find((item: RateLimitRule) => item.id === rateLimitRuleId);
        response = { data: found || null };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/rate-limit-rules/${rateLimitRuleId}`,
        });
      }
      if (!response?.data || _.isEmpty(response.data)) {
        this.setPageLoadingStatus(false);
        this.redirectToList();
        return;
      }
      this.selectedRateLimitRuleData = response.data;
      this.originalRateLimitRuleData = _.cloneDeep(this.selectedRateLimitRuleData);
      this.countByKeyOptions = _.map(this.selectedRateLimitRuleData.key, (keyValue) => {
        return {
          id: DatasetsUtils.generateUUID2(),
          value: keyValue,
        };
      });
      this.setPageLoadingStatus(false);
    },

    async duplicateRateLimitRule() {
      this.setPageLoadingStatus(true);
      const rateLimitRuleToAdd = _.cloneDeep(this.selectedRateLimitRuleData);
      rateLimitRuleToAdd.id = DatasetsUtils.generateUUID2();
      rateLimitRuleToAdd.name = Utils.generateUniqueEntityName(
        rateLimitRuleToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      let response;
      if (shouldUseMockData()) {
        const mockData = getRateLimitRulesMockData();
        if (mockData && Array.isArray(mockData.items)) {
          mockData.items.push(rateLimitRuleToAdd);
        }
        Utils.toast({
          message: successMessage,
          type: 'is-success',
        });
        response = { data: rateLimitRuleToAdd };
      } else {
        const url = `conf/${this.selectedBranch}/rate-limit-rules/${rateLimitRuleToAdd.id}`;
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'POST',
          url,
          data: rateLimitRuleToAdd,
          successMessage,
          failureMessage,
        });
      }
      if (response) {
        this.switchRateLimitRule(rateLimitRuleToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadRateLimitRule() {
      Utils.downloadFile('rate-limitrule', 'json', this.selectedRateLimitRuleData);
    },

    openDeleteRateLimitRuleModal() {
      this.showDeleteRateLimitRuleModal = true;
    },

    async deleteRateLimitRule() {
      this.isDeleteLoading = true;
      const rateLimitRuleId = this.selectedRateLimitRule;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === rateLimitRuleId;
      });
      if (dropdownIndex !== -1) {
        this.dropdownData.splice(dropdownIndex, 1);
      }
      this.selectedRateLimitRule = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      if (shouldUseMockData()) {
        const mockData = getRateLimitRulesMockData();
        if (mockData && Array.isArray(mockData.items)) {
          const index = mockData.items.findIndex(
            (item: RateLimitRule) => item.id === rateLimitRuleId
          );
          if (index !== -1) {
            mockData.items.splice(index, 1);
          }
        }
        Utils.toast({
          message: successMessage,
          type: 'is-success',
        });
      } else {
        await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'DELETE',
          url: `conf/${this.selectedBranch}/rate-limit-rules/${rateLimitRuleId}`,
          successMessage,
          failureMessage,
        });
      }
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      // Update Rate Limit Rule
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedRateLimitRuleData.name = this.selectedRateLimitRuleData.name.trim();
      let response;
      if (shouldUseMockData()) {
        const mockData = getRateLimitRulesMockData();
        if (mockData && Array.isArray(mockData.items)) {
          if (this.isNew) {
            mockData.items.push(this.selectedRateLimitRuleData);
          } else {
            const index = mockData.items.findIndex(
              (item: RateLimitRule) => item.id === this.selectedRateLimitRuleData.id
            );
            if (index !== -1) {
              mockData.items.splice(index, 1, this.selectedRateLimitRuleData);
            }
          }
        }
        Utils.toast({
          message: successMessage,
          type: 'is-success',
        });
        response = { data: this.selectedRateLimitRuleData };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: this.isNew ? 'POST' : 'PUT',
          url: `conf/${this.selectedBranch}/rate-limit-rules/${this.selectedRateLimitRuleData.id}`,
          data: this.selectedRateLimitRuleData,
          successMessage,
          failureMessage,
          displayServerFailureMessage: true,
        });
      }
      // Update connected Security Policies
      const connectionsToAdd = _.filter(this.connections, (connection) => {
        return !_.some(this.connectionsOriginal, (connectionOriginal) => {
          return (
            connection.securityPolicyId === connectionOriginal.securityPolicyId &&
            connection.entryId === connectionOriginal.entryId
          );
        });
      });
      const connectionsToRemove = _.filter(this.connectionsOriginal, (connection) => {
        return !_.some(this.connections, (connectionOriginal) => {
          return (
            connection.securityPolicyId === connectionOriginal.securityPolicyId &&
            connection.entryId === connectionOriginal.entryId
          );
        });
      });
      const connectionsToUpdate = _.uniqBy(
        _.concat(connectionsToAdd, connectionsToRemove),
        'securityPolicyId'
      );
      await Promise.all(
        connectionsToUpdate.map(async (connection) => {
          const securityPolicy = this.getSecurityPolicy(connection.securityPolicyId);
          if (shouldUseMockData()) {
            const mockSP = getSecurityPoliciesMockData();
            if (mockSP && Array.isArray(mockSP.items)) {
              const index = mockSP.items.findIndex(
                (item: SecurityPolicy) => item.id === securityPolicy.id
              );
              if (index !== -1) {
                mockSP.items.splice(index, 1, securityPolicy);
              }
            }
          } else {
            await RequestsUtils.sendRequest({
              host: this.userPlanet,
              methodName: 'PUT',
              url: `conf/${this.selectedBranch}/security-policies/${securityPolicy.id}`,
              data: securityPolicy,
            });
          }
        })
      );
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedRateLimitRuleData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          // Reload dropdown list
          await this.loadDropdownData();
          this.originalRateLimitRuleData = _.cloneDeep(this.selectedRateLimitRuleData);
        }
      }
      // Reload connections
      await this.loadSecurityPolicies();
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      const displayedDefaultIds = [
        'action-rate-limit-block',
        'action-waap-feed-block',
        'action-https-redirect',
        'action-challenge',
        'action-monitor',
        'action-skip',
      ];
      const displayedTypes: Action['type'][] = ['block', 'challenge', 'ichallenge', 'monitor'];
      let response;
      if (shouldUseMockData()) {
        response = { data: getActionsMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/actions`,
          config: { headers: { 'x-fields': 'id, name, type' } },
        });
      }
      this.actions = _.filter(response?.data?.items, (item: Action) => {
        const hiddenDefaultId =
          item.id.startsWith('action-') && !displayedDefaultIds.includes(item.id);
        return displayedTypes.includes(item.type) && !hiddenDefaultId;
      });
      this.setPageLoadingStatus(false);
    },

    addKey() {
      this.countByKeyOptions.push({
        id: DatasetsUtils.generateUUID2(),
        value: {
          attrs: 'ip',
        },
      });
    },

    removeKey(index: number) {
      if (this.countByKeyOptions.length > 1) {
        this.countByKeyOptions.splice(index, 1);
      }
    },

    normalizeBanTtl() {
      if (_.isNil(this.selectedRateLimitRuleData.ttl)) {
        this.selectedRateLimitRuleData.ttl = 60 * 60;
      }
    },

    async loadSecurityPolicies() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getSecurityPoliciesMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/security-policies`,
        });
      }
      if (response) {
        this.securityPolicies = Utils.sortArrayByName(response?.data?.items) as SecurityPolicy[];
        this.loadConnections();
      }
      this.setPageLoadingStatus(false);
    },

    loadConnections() {
      this.connections = _.sortBy(
        _.flatMap(
          _.filter(this.securityPolicies, (securityPolicy) => {
            return _.some(securityPolicy.map, (mapEntry: SecurityPolicyEntryMatch) => {
              return mapEntry['rate_limit_rules'].includes(this.selectedRateLimitRuleData.id);
            });
          }),
          (securityPolicy) => {
            return _.compact(
              _.map(securityPolicy.map, (mapEntry) => {
                if (mapEntry['rate_limit_rules'].includes(this.selectedRateLimitRuleData.id)) {
                  return {
                    name: securityPolicy.name,
                    id: `${securityPolicy.id}-${mapEntry.id}`,
                    securityPolicyId: securityPolicy.id,
                    entryId: mapEntry.id,
                    entryName: mapEntry.name,
                    entryMatch: mapEntry.match,
                  } as Connection;
                } else {
                  return null;
                }
              })
            );
          }
        )
      );
      this.connectionsOriginal = _.cloneDeep(this.connections);
    },

    getConnectionDefaultValues(): NewConnection {
      const [defaultSecurityPolicy] = this.newConnectionSecurityPolicies || [];
      const securityPolicyId = defaultSecurityPolicy?.id;
      const connectionLevel: ConnectionLevel = 'site';
      const entryIds = ['__site_level__'];
      return {
        securityPolicyId,
        connectionLevel,
        entryIds,
      };
    },

    addConnection(newConnection: NewConnection) {
      if (!newConnection.securityPolicyId) {
        return;
      }
      const securityPolicies =
        newConnection.securityPolicyId === 'rbz-add-all-security-policies-option'
          ? (this.newConnectionSecurityPolicies.filter(
              (securityPolicy) => securityPolicy.id !== 'rbz-add-all-security-policies-option'
            ) as SecurityPolicy[])
          : [this.getSecurityPolicy(newConnection.securityPolicyId)];
      securityPolicies.forEach((securityPolicy: SecurityPolicy) => {
        let entries: SecurityPolicyEntryMatch['id'][] = [];
        const connectedSiteLevel = this.newConnectionCheckConnectedSiteLevel(securityPolicy.id);
        const connectedAnyEntry = this.newConnectionCheckConnectedAnyEntry(securityPolicy.id);
        if (newConnection.connectionLevel === 'path' && !connectedSiteLevel) {
          entries = newConnection.entryIds;
        }
        if (newConnection.connectionLevel === 'site' && !connectedAnyEntry) {
          entries = ['__site_level__'];
        }
        if (newConnection.connectionLevel === 'all' && !connectedSiteLevel) {
          entries = _.map(securityPolicy.map, 'id').filter((id: SecurityPolicyEntryMatch['id']) => {
            return id !== '__site_level__';
          });
        }
        entries.forEach((id: string) => {
          const included = _.some(this.connections, (connection) => {
            return connection.securityPolicyId === securityPolicy.id && connection.entryId === id;
          });
          if (!included) {
            const mapEntry = securityPolicy?.map.find((entry) => {
              return entry.id === id;
            });
            this.connections.push({
              name: securityPolicy.name,
              id: `${securityPolicy.id}-${mapEntry.id}`,
              securityPolicyId: securityPolicy.id,
              entryId: mapEntry.id,
              entryName: mapEntry.name,
              entryMatch: mapEntry.match,
            });
            mapEntry['rate_limit_rules'].push(this.selectedRateLimitRuleData.id);
          }
        });
      });
    },

    removeConnection(connectionToRemove: Connection) {
      this.connections = _.filter(this.connections, (connection) => {
        return (
          connection.securityPolicyId !== connectionToRemove.securityPolicyId ||
          connection.entryId !== connectionToRemove.entryId
        );
      });
      const securityPolicy = this.getSecurityPolicy(connectionToRemove.securityPolicyId);
      const mapEntry = securityPolicy?.map.find((entry) => {
        return entry.id === connectionToRemove.entryId;
      });
      mapEntry['rate_limit_rules'] = _.filter(mapEntry['rate_limit_rules'], (limitId) => {
        return limitId !== this.selectedRateLimitRuleData.id;
      });
    },

    cancelAddConnection() {
      this.$refs.connectionsTable.setShowNewRow(false);
    },

    getSecurityPolicy(securityPolicyId: SecurityPolicy['id']): SecurityPolicy {
      return this.securityPolicies?.find((securityPolicy) => {
        return securityPolicy.id === securityPolicyId;
      });
    },

    newConnectionListSecurityPolicyEntries(
      securityPolicyId: SecurityPolicy['id']
    ): SecurityPolicyEntryMatch[] {
      const securityPolicy = this.getSecurityPolicy(securityPolicyId);
      return _.filter(securityPolicy?.map, (securityPolicyEntry: SecurityPolicyEntryMatch) => {
        return (
          securityPolicyEntry.id !== '__site_level__' &&
          !securityPolicyEntry['rate_limit_rules'].includes(this.selectedRateLimitRuleData.id)
        );
      });
    },

    newConnectionCheckConnectedSiteLevel(securityPolicyId: SecurityPolicy['id']) {
      const securityPolicy = this.getSecurityPolicy(securityPolicyId);
      const siteLevelEntry = _.find(securityPolicy.map, ['id', '__site_level__']);
      return _.includes(siteLevelEntry?.['rate_limit_rules'], this.selectedRateLimitRuleData.id);
    },

    newConnectionCheckConnectedAnyEntry(securityPolicyId: SecurityPolicy['id']) {
      const securityPolicy = this.getSecurityPolicy(securityPolicyId);
      return _.some(securityPolicy?.map, (securityPolicyEntry: SecurityPolicyEntryMatch) => {
        return (
          securityPolicyEntry.id !== '__site_level__' &&
          _.includes(securityPolicyEntry?.['rate_limit_rules'], this.selectedRateLimitRuleData.id)
        );
      });
    },

    newConnectionOnChangeSecurityPolicy(newConnection: NewConnection) {
      if (newConnection.securityPolicyId === 'rbz-add-all-security-policies-option') {
        this.newConnectionOnChangeConnectionLevel(newConnection, 'site');
        return;
      }
      const isConnectedSiteLevel = this.newConnectionCheckConnectedAnyEntry(
        newConnection.securityPolicyId
      );
      if (newConnection.connectionLevel === 'path') {
        this.newConnectionOnChangeConnectionLevel(newConnection, 'path');
      } else if (newConnection.connectionLevel === 'site' && isConnectedSiteLevel) {
        this.newConnectionOnChangeConnectionLevel(newConnection, 'path');
      }
    },

    newConnectionOnChangeConnectionLevel(
      newConnection: NewConnection,
      newLevel: NewConnection['connectionLevel']
    ) {
      newConnection.connectionLevel = newLevel;
      switch (newConnection.connectionLevel) {
        case 'path': {
          newConnection.entryIds = [
            this.newConnectionListSecurityPolicyEntries(newConnection.securityPolicyId)?.[0].id,
          ];
          break;
        }
        case 'site': {
          newConnection.entryIds = ['__site_level__'];
          break;
        }
        case 'all': {
          newConnection.entryIds = [];
          break;
        }
      }
    },

    updateRelation(fieldName: IncludeExcludeType, newRelation: Relation): void {
      this.selectedRateLimitRuleData[fieldName].relation = newRelation;
    },

    updateTags(fieldName: IncludeExcludeType, tags: string[]): void {
      this.selectedRateLimitRuleData[fieldName].tags = tags;
    },

    setSessionDataWatcher() {
      this.$watch(
        () =>
          [this.selectedRateLimitRuleData, this.entityNotSaved] as [RateLimitRule | null, boolean],
        ([formData, entityNotSaved]: [RateLimitRule | null, boolean]) => {
          Utils.saveSessionData(formData as GenericObject, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  created() {
    const tagsStore = useTagsStore();
    this.$watch(
      () => [tagsStore.editorDocId, tagsStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'rate-limit-rules') {
          return;
        }
        await this.branchesStore.getBranches();
        await this.loadActions();
        await this.loadRateLimitRule(docId);
      },
      { immediate: true }
    );
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Rate Limit Rule', 'Rate Limit Rules Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          const dropdownDataPromise = this.loadDropdownData();
          const actionsPromise = this.loadActions();
          await Promise.all([dropdownDataPromise, actionsPromise]);
          if (this.isNew) {
            this.selectedRateLimitRuleData = this.newRateLimitRule();
            this.selectedRateLimitRuleData.name = Utils.generateUniqueEntityName(
              this.selectedRateLimitRuleData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalRateLimitRuleData = _.cloneDeep(this.selectedRateLimitRuleData);
            this.countByKeyOptions = _.map(this.selectedRateLimitRuleData.key, (keyValue) => {
              return {
                id: DatasetsUtils.generateUUID2(),
                value: keyValue,
              };
            });
            await this.loadSecurityPolicies();
          } else {
            const rateLimitRuleId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === rateLimitRuleId;
            });
            if (dataExists) {
              await this.loadRateLimitRule(rateLimitRuleId);
              await this.loadSecurityPolicies();
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedRateLimitRuleData = JSON.parse(savedSessionData);
            this.countByKeyOptions = _.map(this.selectedRateLimitRuleData.key, (keyValue) => {
              return {
                id: DatasetsUtils.generateUUID2(),
                value: keyValue,
              };
            });
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.all-connected-data-cell,
.new-data-cell {
  color: map.get(colors.$colors, 'shuttle-gray');
  vertical-align: middle;
}

.connection-level-radio-wrapper {
  display: flex;
  gap: 16px;

  :deep(.radio-label) {
    color: map.get(colors.$colors, 'bright-gray');
  }
}
</style>
